<script setup lang="ts">

import {ref} from "vue";

import {useReCaptcha} from "vue-recaptcha-v3";
import {useI18n} from "vue-i18n";
import * as CoreService from "@/services/core.service";
import CustomButton from "@/components/Button/Button.vue";
import DateInput from "@/components/Global/DateInput.vue";
import {trackEvent} from "@/services/event.service";
import {usePageSEO} from "@/composables/usePageSEO";
import { utils } from '@/services/client'
import {VPhoneInput} from 'v-phone-input'
import 'v-phone-input/dist/v-phone-input.css'
import {parsePhoneNumber} from 'awesome-phonenumber'
import {useLocalization} from "@/locales/localization";

const formatDate = utils.formatDate;
const {executeRecaptcha, recaptchaLoaded} = useReCaptcha()
const { currentLocale } = useLocalization();


const {t} = useI18n(); // Import the translation function
usePageSEO('inquiry')

//TODO translate
const boatTypes = ref(["Sailboat", "Catamaran", "Motorboat", "Gulet"]);
const loading = ref(false);
const form = ref({})

const formData = ref({
  destination: "",
  dateStart: null,
  dateEnd: null,
  duration: "",
  layout: "",
  passengers: "2",
  cabins: "1",
  name: "",
  telephone: "",
  email: "",
  message: "",
  agreed: false,
});

const inputs = ref([
  {
    label: t('assistance.items[0].label'),
    placeholder: t('assistance.items[0].placeholder')
  },
  {
    label: t('assistance.items[1].label'),
    placeholder: t('assistance.items[1].placeholder')
  },
  {
    label: t('assistance.items[2].label'), // Boat Type and Layout
    placeholder: t('assistance.items[2].placeholder')
  },
  {
    label: t('assistance.items[3].label'), // Passengers
    placeholder: t('assistance.items[3].placeholder')
  },
  {
    label: t('assistance.items[4].label'),
    placeholder: t('assistance.items[4].placeholder')
  },
  {
    label: t('assistance.items[5].label'), //name
    placeholder: t('assistance.items[5].placeholder')
  },
  {
    label: t('assistance.items[6].label'), //Telephone
    placeholder: t('assistance.items[6].placeholder')
  },
  {
    label: t('assistance.items[7].label'), //email
    placeholder: t('assistance.items[7].placeholder')
  },
  {
    label: t('assistance.items[8].label'),
    placeholder: t('assistance.items[8].placeholder')
  }
]);

const valid = ref(true)
const state = ref<'idle' | 'submit' | 'error'>('idle');

const requestHelp = () => {
  form.value.validate().then(async (result) => {
    if (!result.valid) {
      return;
    }

    trackEvent('cosmosy.ga.yachtPage.sendInquiry', { 
      event_label: 'Send Inquiry',

      userData: {
        fullname: formData.value.name,
        email: formData.value.email,
        phone: parsePhoneNumber(formData.value.telephone).number?.international,
        boat_type: formData.value.layout,
        destination: formData.value.destination,
      }
      
    });

    loading.value = true;

    try {
      await recaptchaLoaded();
      await executeRecaptcha("help_request");

      const inquiryPayload = {
        destination: formData.value.destination,
        dateStart: formatDate(formData.value.dateStart),
        dateEnd: formatDate(formData.value.dateEnd),
        layout: formData.value.layout,
        passengers: formData.value.passengers,
        cabins: formData.value.cabins,
        name: formData.value.name,
        telephone: parsePhoneNumber(formData.value.telephone).number?.international,
        email: formData.value.email,
        message: formData.value.message,
        agreed: formData.value.agreed
      }

      await CoreService.sendInquiryRequest(inquiryPayload, currentLocale.value);

      state.value = "submit";

      // Clear the form
      Object.keys(formData.value).forEach((key) => {
        if (key === "dateStart" || key === "dateEnd") {
          formData.value[key] = null; // Reset dates
        } else if (typeof formData.value[key] === "boolean") {
          formData.value[key] = false; // Reset checkboxes
        } else {
          formData.value[key] = ""; // Reset text fields
        }
      });

      // Reset form validation
      form.value.reset();

      // Hide success message after 5 seconds
      setTimeout(() => {
        state.value = "idle";
      }, 5000);
    } catch (error) {
      state.value = "error"; // Handle error state
    } finally {
      loading.value = false;
    }
  });
};
</script>


<template>
  <div class="bg-background-light border-b-[1px] border-bw-grey min-h-screen">
    <!-- Form -->
    <div class="container mx-auto py-8 md:py-12 ">
      <div class="bg-gradient-to-r rounded-8xl from-gradients-ghostWhite to-gradients-aliceBlue p-8 md:p-12">
        <div class="w-full md:w-2/3 lg:w-1/2 mx-auto flex flex-col gap-4 py-12">
          <!-- Form Title -->
          <h1 class="text-2xl font-bold text-gray-700 ">{{ t('assistance.formHeader') }}</h1>
          <p class="responsive-body-medium text-gray-500 " v-html="t('assistance.formText')">
            
          </p>

          <v-form ref="form" v-model="valid"
                  class="mt-10 flex flex-col gap-6">

            <div class="flex flex-col gap-2">
              <div class="responsive-body-large">{{ t('assistance.whereBegin') }}</div>
              <VTextField
                  v-model="formData.destination"
                  :label= inputs[0].label
                  type="text"
                  variant="underlined"
                  :placeholder=inputs[0].placeholder
                  required
                  :disabled="loading"
                  :rules="[
                      v => !!v || t('assistance.fillRequired'),
                      v => /^[\p{L}\p{M} ]+$/u.test(v) || t('assistance.validValue')
                  ]"
              />
            </div>
            <!-- Start Date and Duration -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div class="flex flex-col gap-2">
                <div class="responsive-body-large">{{ t('assistance.whenTravel') }}</div>
                <DateInput v-model:dateFrom="formData.dateStart"
                           v-model:dateTo="formData.dateEnd"
                           :columns="1"
                           :rows="1"
                >
                  <template #display="slotProps">
                    <VTextField
                        :label= inputs[1].label
                        variant="underlined"
                        required
                        readonly
                        @click="slotProps.toggleOpen"
                        :model-value="slotProps.startString+'-'+slotProps.endString"
                        :rules="[
                        () => !!formData.dateStart && !!formData.dateEnd || t('assistance.fillRequired')
                      ]"
                    />
                  </template>
                </DateInput>
              </div>
              
              <div class="flex flex-col gap-2">
                <div class="responsive-body-large">{{ t('assistance.preferredType') }}</div>
                <VSelect
                    v-model="formData.layout"
                    :label= inputs[2].label
                    :items="boatTypes"
                    :placeholder=inputs[2].placeholder
                    variant="underlined"
                    required
                    :disabled="loading"
                    :rules="[v => !!v || t('assistance.fillRequired')]"
                />
              </div>
        
            </div>
            <!-- People and Cabins -->
            
            <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div class="flex flex-col gap-2">
                <div class="responsive-body-large">{{ t('assistance.guests') }}</div>
                <div class="flex flex-col gap-2">
                  <VTextField
                      v-model="formData.passengers"
                      :label= inputs[3].label
                      type="number"
                      variant="underlined"
                      :placeholder= inputs[3].placeholder
                      required
                      :disabled="loading"
                      @input="formData.passengers = Math.max(0, formData.passengers)"
                      :rules="[v => !!v || t('assistance.fillRequired')]"
                  />
                </div>
              </div>


              <div class="flex flex-col gap-2">
                <div class="responsive-body-large">{{ t('assistance.cabinsNeeded') }}</div>
                <VTextField
                    v-model="formData.cabins"
                    :label= inputs[4].label
                    type="number"
                    variant="underlined"
                    :placeholder= inputs[4].placeholder
                    required
                    :disabled="loading"
                    @input="formData.cabins = Math.max(0, formData.cabins)"
                    :rules="[v => !!v || t('assistance.fillRequired')]"
                />
              </div>

            </div>
            <!-- Contact Info Section -->
            <div>
              <h3 class="text-lg font-semibold text-gray-700">{{ t('assistance.contactDetails') }}</h3>
            </div>

            <div>
              <div class="responsive-body-large">{{ t('assistance.fullName') }}</div>
              <VTextField
                  v-model="formData.name"
                  :label= inputs[5].label
                  type="text"
                  variant="underlined"
                  :placeholder= inputs[5].placeholder
                  required
                  :disabled="loading"
                  :rules="[
                      v => !!v || t('assistance.fillRequired'),
                      v => /^[\p{L}\p{M} ]+$/u.test(v) || t('assistance.validValue')
                  ]"

              />
            </div>

            <!-- Telephone and Email -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div class="flex flex-col gap-2">
                <div class="responsive-body-large">{{ t('assistance.phoneNumber') }}</div>
                  <v-phone-input
                      v-model="formData.telephone"
                      :default-country="'GR'"
                      :invalidMessage = "t('enterValidPhone')"
                      :label= inputs[6].label
                      :only-country-code="false"
                      :return-object="true"
                      required
                      :disabled="loading"
                      density="comfortable"
                      :use-default-validation="false"
                      :rules="[
                      v => !!v || t('assistance.fillRequired'),
                      v => /^[\d+ ]+$/.test(v) || t('assistance.validValue')
                  ]"

                />
              </div>

              <div class="flex flex-col gap-2">
                <div class="responsive-body-large">{{ t('assistance.emailAddress') }}</div>
                <VTextField
                    v-model="formData.email"
                    :label= inputs[7].label
                    type="email"
                    variant="underlined"
                    :placeholder= inputs[7].placeholder
                    required
                    :disabled="loading"
                    :rules="[
                            v => !!v || t('assistance.fillRequired'),
                      v => /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(v) || t('assistance.validValue')
                  ]"
                />
              </div>

            </div>

            <!-- Message -->
            <div class="flex flex-col gap-2">
              <div class="responsive-body-large">{{ t('assistance.tailorOffer') }}</div>
              <VTextarea
                  v-model="formData.message"
                  :label= inputs[8].label
                  type="textarea"
                  variant="underlined"
                  :placeholder= inputs[8].placeholder
                  rows="4"
                  :disabled="loading"
              />
            </div>
            <!-- Message -->
            <!-- We go this message here

                2025-04-15

                Γεια σου Φώτη, μπορείς σε παρακαλώ στο https://www.cosmosyachting.com/en/inquiry
                το πεδίο message να μην είναι υποχρεωτικό και
                να βγάλεις από κάτω αυτό που λέει I agree tot he privacy policy.

                ευχαριστώ

                Υ.Γ. Η λίστα, το report είναι τέλειο

                check the diff to revert the code in the future
            -->

          </v-form>

          <div class="flex flex-auto my-3 gap-3">

            <CustomButton type="black"
                          :rounded="true"
                          :outlined="false"
                          :disabled="loading"
                          class="flex-1"
                          @click="requestHelp"
            >
              {{ t('assistance.submit') }}
            </CustomButton>

          </div>
          <div class="responsive-body-small">{{ t('assistance.handledPersonally') }}</div>


          <div v-if="state === 'submit'" class="text-green-600 mt-4">
            {{ t('assistance.thanksForMessage') }}
          </div>
          <div v-if="state === 'error'" class="text-red-600 mt-4">
            {{ t('assistance.unexpectedError') }}
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.form-input {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  background-color: #fff;
  outline: none;
  transition: border-color 0.2s;
}

.form-input:focus {
  border-color: #1b84ff;
  box-shadow: 0 0 0 1px #1b84ff;
}

textarea.form-input {
  min-height: 100px;
}

textarea.form-input::placeholder,
input.form-input::placeholder {
  color: #9ca3af;
}

::v-deep(.v-field__input input) {
  background-color: transparent !important;
}


</style>
